import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography,
    Divider, CircularProgress, Button, IconButton, FormControlLabel, Switch,
    Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { formatDate } from '../utils';

const InfoRow = ({ label, value, children }) => {
    if (!value && !children) return null;
    return (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 0.9, alignItems: 'baseline' }}>
            <Typography
                variant="caption"
                sx={{
                    minWidth: 76, color: 'text.secondary', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.6, flexShrink: 0,
                    fontSize: '0.65rem',
                }}
            >
                {label}
            </Typography>
            {children || (
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            )}
        </Box>
    );
};

const FamilyLink = ({ id, name }) => {
    if (!name) return null;
    return id
        ? <Typography variant="body2" component="a" href={`/tree/${id}`}
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {name}
          </Typography>
        : <Typography variant="body2">{name}</Typography>;
};

const TreeView = () => {
    const [person, setPerson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dot, setDot] = useState('');
    const [levels, setLevels] = useState({ ancestors: 1, descendants: 1 });
    const [maxDepth, setMaxDepth] = useState({ ancestors: 1, descendants: 1 });
    const [showSpouses, setShowSpouses] = useState(true);
    const [rankdir, setRankdir] = useState('TB');
    const [photoError, setPhotoError] = useState(false);
    
    // Relatives exploration
    const [relatives, setRelatives] = useState([]);
    const [relativesTitle, setRelativesTitle] = useState('');
    const [relativesOpen, setRelativesOpen] = useState(false);
    const [relativesLoading, setRelativesLoading] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();
    const graphRef = useRef(null);

    useEffect(() => {
        setPhotoError(false);
        axios.get(`/api/people/${id}`)
            .then(response => setPerson(response.data))
            .catch(error => console.error(`Error fetching person ${id}:`, error));
        axios.get(`/api/depth/${id}`)
            .then(response => setMaxDepth(response.data))
            .catch(error => console.error(`Error fetching depth ${id}:`, error));
    }, [id]);

    useEffect(() => {
        setLoading(true);
        axios.get(`/api/tree/${id}?ancestors=${levels.ancestors}&descendants=${levels.descendants}&spouses=${showSpouses}&rankdir=${rankdir}`)
            .then(response => { setDot(response.data); setLoading(false); })
            .catch(error => { console.error(`Error fetching tree ${id}:`, error); setLoading(false); });
    }, [id, levels, showSpouses, rankdir]);

    useEffect(() => {
        if (graphRef.current && dot) {
            graphRef.current.innerHTML = dot;
            const nodes = graphRef.current.querySelectorAll('.node');
            nodes.forEach(node => {
                node.addEventListener('click', (e) => {
                    e.preventDefault();
                    const anchor = node.querySelector('a');
                    if (!anchor) return;
                    const href = anchor.getAttribute('href') ||
                                 anchor.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
                    if (!href) return;
                    navigate(`/tree/${href.split('/').pop()}`);
                });
            });
        }
    }, [dot, navigate]);

    const fetchRelatives = (type, degree, title) => {
        setRelativesTitle(title);
        setRelativesOpen(true);
        setRelativesLoading(true);
        axios.get(`/api/people/${id}/relatives?type=${type}&degree=${degree}`)
            .then(res => {
                setRelatives(res.data);
                setRelativesLoading(false);
            })
            .catch(err => {
                console.error(err);
                setRelativesLoading(false);
            });
    };

    if (!person) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const birthDate = formatDate(person.birthday);
    const deathDate = formatDate(person.decease);
    const hasLocation = person.city || person.province || person.country;
    const hasPersonal = person.profession || person.observations;
    const hasFamily   = person.father || person.mother || person.spouses?.length || person.children?.length;

    return (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>

            {/* ── Sidebar: ficha ── */}
            <Card sx={{ width: { xs: '100%', md: 290 }, flexShrink: 0, position: { xs: 'static', md: 'sticky' }, top: 20, overflow: 'hidden' }}>
                {person.has_photo && !photoError && (
                    <Box
                        component="img"
                        src={`/fotos/${person.id_person}.jpg`}
                        alt={`${person.firstname} ${person.lastname}`}
                        onError={() => setPhotoError(true)}
                        sx={{ width: '100%', display: 'block' }}
                    />
                )}
                <CardContent sx={{ px: 2.5, pb: '20px !important' }}>

                    {/* Nombre y fechas */}
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mb: 0.4 }}>
                        {person.firstname} {person.lastname}
                    </Typography>
                    {(birthDate || deathDate) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                            {birthDate && <span>* {birthDate}</span>}
                            {birthDate && deathDate && <br />}
                            {deathDate && <span>† {deathDate}</span>}
                        </Typography>
                    )}

                    {/* Ubicación */}
                    {hasLocation && (
                        <>
                            <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                            <InfoRow label="Ciudad"    value={person.city} />
                            <InfoRow label="Provincia" value={person.province} />
                            <InfoRow label="País"      value={person.country} />
                        </>
                    )}

                    {/* Personal */}
                    {hasPersonal && (
                        <>
                            <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                            <InfoRow label="Profesión" value={person.profession} />
                            <InfoRow label="Notas"     value={person.observations} />
                        </>
                    )}

                    {/* Familia */}
                    {hasFamily && (
                        <>
                            <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                            <InfoRow label="Padre">
                                <FamilyLink id={person.id_father} name={person.father} />
                            </InfoRow>
                            <InfoRow label="Madre">
                                <FamilyLink id={person.id_mother} name={person.mother} />
                            </InfoRow>
                            {person.spouses?.length > 0 && person.spouses.map((s, idx) => (
                                <React.Fragment key={s.id_person || idx}>
                                    <Divider sx={{ my: 1, borderColor: 'rgba(0,0,0,0.04)' }} />
                                    <InfoRow label="Cónyuge">
                                        <FamilyLink
                                            id={s.id_person}
                                            name={`${s.firstname} ${s.lastname}`}
                                        />
                                    </InfoRow>
                                    {s.marriage && (
                                        <InfoRow label="Casamiento" value={formatDate(s.marriage)} />
                                    )}
                                </React.Fragment>
                            ))}
                            {person.children?.length > 0 && (
                                <InfoRow label="Hijos">
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                        {person.children.map(c => (
                                            <FamilyLink key={c.id_person} id={c.id_person} name={`${c.firstname} ${c.lastname}`} />
                                        ))}
                                    </Box>
                                </InfoRow>
                            )}
                        </>
                    )}
                    <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => navigate(`/people/${id}/edit`)}
                    >
                        Editar
                    </Button>

                    <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.08)' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mb: 1, display: 'block' }}>
                        EXPLORAR PARENTESCOS
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('uncles', 1, 'Tíos')}
                        >
                            Ver Tíos
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('grandparents', 1, 'Abuelos')}
                        >
                            Ver Abuelos
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('grandchildren', 1, 'Nietos')}
                        >
                            Ver Nietos
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('cousins', 1, 'Primos Hermanos')}
                        >
                            Ver Primos Hermanos
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('cousins', 2, 'Primos Segundos')}
                        >
                            Ver Primos Segundos
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('inlaws', 1, 'Cuñados')}
                        >
                            Ver Cuñados
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ justifyContent: 'flex-start', fontSize: '0.75rem' }}
                            onClick={() => fetchRelatives('con-inlaws', 1, 'Concuñados')}
                        >
                            Ver Concuñados
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={relativesOpen} onClose={() => setRelativesOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700 }}>{relativesTitle}</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {relativesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={30} /></Box>
                    ) : (
                        <List disablePadding>
                            {relatives.length === 0 ? (
                                <ListItem><ListItemText primary="No se encontraron registros" /></ListItem>
                            ) : (
                                relatives.map((r, idx) => (
                                    <ListItem
                                        key={r.id_person}
                                        component={Link}
                                        to={`/tree/${r.id_person}`}
                                        onClick={() => setRelativesOpen(false)}
                                        divider={idx < relatives.length - 1}
                                        sx={{
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                                        }}
                                    >
                                        <ListItemText
                                            primary={`${r.firstname} ${r.lastname}`}
                                            secondary={[formatDate(r.birthday), formatDate(r.decease)].filter(Boolean).join(' — ')}
                                        />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    )}
                </DialogContent>
                <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={() => setRelativesOpen(false)}>Cerrar</Button>
                </Box>
            </Dialog>

            {/* ── Panel principal: árbol ── */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>

                {/* Controles */}
                <Box sx={{ display: 'flex', gap: 3, mb: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    {[
                        { label: '↑ Ancestros',     key: 'ancestors' },
                        { label: '↓ Descendientes', key: 'descendants' },
                    ].map(({ label, key }) => (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                                {label}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setLevels(l => ({ ...l, [key]: l[key] - 1 }))}
                                disabled={levels[key] <= 0}
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: '2px' }}
                            >
                                <Typography sx={{ fontSize: 16, lineHeight: 1, px: 0.3 }}>−</Typography>
                            </IconButton>
                            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>
                                {levels[key]}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setLevels(l => ({ ...l, [key]: l[key] + 1 }))}
                                disabled={levels[key] >= maxDepth[key]}
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: '2px' }}
                            >
                                <Typography sx={{ fontSize: 16, lineHeight: 1, px: 0.3 }}>+</Typography>
                            </IconButton>
                        </Box>
                    ))}
                    <FormControlLabel
                        control={<Switch checked={showSpouses} onChange={e => setShowSpouses(e.target.checked)} size="small" />}
                        label={<Typography variant="body2" color="text.secondary">Cónyuges</Typography>}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setRankdir(r => r === 'TB' ? 'LR' : 'TB')}
                        sx={{ minWidth: 0, px: 1.5 }}
                        title={rankdir === 'TB' ? 'Cambiar a horizontal' : 'Cambiar a vertical'}
                    >
                        {rankdir === 'TB' ? '↔' : '↕'}
                    </Button>
                    {loading && <CircularProgress size={22} thickness={4} sx={{ color: 'secondary.main' }} />}
                </Box>

                {/* SVG del árbol */}
                <Box
                    ref={graphRef}
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(60,40,20,0.08)',
                        '& svg': { display: 'block', maxWidth: '100%', height: 'auto' },
                        '& .node': { cursor: 'pointer' },
                        '& .node:hover': { opacity: 0.82, transition: 'opacity 0.15s' },
                    }}
                />
            </Box>
        </Box>
    );
};

export default TreeView;
